import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TableCell, TableRow } from '@cbhq/cds-web/tables';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';
import { memo, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { AssetConfig } from '@/onchain/assets';
import { PoolsListTokensMedia } from '@/components/Pools/PoolsListTokensMedia';
import { usePoolsInfo } from '@/components/Pools/hooks/usePoolsInfo';
import { DisplayType, format } from '@/utils/numberFormatter';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';
import { getOrderedCurrenciesForPool } from '@/utils/tokenPreference';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/utils/featureFlags';

type Props = {
  pool: ReturnType<typeof usePoolsInfo>['data']['poolsInfo'][number];
};

export const PoolsListDesktopRow = memo(function PoolsListDesktopRow({ pool }: Props) {
  const isFeatureFlagEnabled = useFeatureFlag(
    FEATURE_FLAGS.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_POOL_DETAILS_PAGE,
  );
  const router = useRouter();
  const {
    poolId,
    currencies,
    name,
    fee,
    tvlNotional,
    volume24hNotional,
    volume7dNotional,
    fees24hNotional,
    fees7dNotional,
    apr7dPercentage,
  } = pool;

  const feePercent = useMemo(() => {
    // fee / fee basis points / percent representation
    return fee / 10_000 / 100;
  }, [fee]);

  // Use shared utility to order currencies consistently with pool name
  const orderedCurrencies = useMemo(() => {
    return getOrderedCurrenciesForPool(currencies);
  }, [currencies]);

  const isUSDCTokenPair = currencies.some((currency) => currency.displaySymbol === 'USDC');

  const handleRowClick = useCallback(() => {
    if (!isFeatureFlagEnabled) return;
    void router.push(`/pools/${poolId}`);
  }, [router, poolId]);

  return (
    <TableRow onClick={handleRowClick} testID={`pool-row-${poolId}`}>
      <TableCell
        start={<PoolsListTokensMedia assets={orderedCurrencies as AssetConfig[]} />}
        title={
          (
            <VStack alignItems="left" gap={0.5}>
              <TextHeadline as="span" noWrap>
                {name}
              </TextHeadline>
              <HStack gap={0.5}>
                <Tag intent="informational" colorScheme="gray">
                  {format({ value: feePercent, type: DisplayType.Percentage })}
                </Tag>
                {isUSDCTokenPair && <USDCRewardsChip />}
              </HStack>
            </VStack>
          ) as unknown as string
        }
      />
      <TableCell alignItems="flex-end">
        <VStack justifyContent="flex-end">
          <TextBody as="span">
            {format({ value: tvlNotional, type: DisplayType.FiatShort })}
          </TextBody>
        </VStack>
      </TableCell>
      <TableCell alignItems="flex-end">
        <VStack justifyContent="flex-end">
          <TextBody as="span">
            {format({ value: apr7dPercentage, type: DisplayType.Percentage })}
          </TextBody>
        </VStack>
      </TableCell>
      <TableCell alignItems="flex-end">
        <VStack justifyContent="flex-end">
          <TextBody as="span">
            {format({ value: volume24hNotional, type: DisplayType.FiatShort })}
          </TextBody>
        </VStack>
      </TableCell>
      <TableCell alignItems="flex-end">
        <VStack justifyContent="flex-end">
          <TextBody as="span">
            {format({ value: volume7dNotional, type: DisplayType.FiatShort })}
          </TextBody>
        </VStack>
      </TableCell>
      <TableCell alignItems="flex-end">
        <VStack justifyContent="flex-end">
          <TextBody as="span">
            {format({ value: fees24hNotional, type: DisplayType.FiatShort })}
          </TextBody>
        </VStack>
      </TableCell>
      <TableCell alignItems="flex-end">
        <VStack justifyContent="flex-end">
          <TextBody as="span">
            {format({ value: fees7dNotional, type: DisplayType.FiatShort })}
          </TextBody>
        </VStack>
      </TableCell>
    </TableRow>
  );
});
